import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
	getAgentToken,
	joinChat,
	sendAgentMessage,
	endChat,
	postAgentPresence,
} from "../../services/liveChatApi";
import { createPieSocketClient } from "../../services/liveChatSocket";
import { PostData } from "../../services/FetchData";
import {
	chatIdOf,
	roomIdOf,
	inboxItemToSession,
	normalizePieSocketMessage,
	upsertChatMessage,
	mergeConversationThread,
} from "../../services/liveChatUtils";

function sessionMergeKey(session) {
	const chatId = chatIdOf(session);
	if (chatId) return `chat:${chatId}`;
	const agentSessionId = session?.agentSessionId || session?.sessionId;
	if (agentSessionId) return `agent:${agentSessionId}`;
	return null;
}

function isHistoricalSession(session) {
	if (!session) return false;
	const st = String(session.status || "").toLowerCase();
	return st === "closed" || st === "ended";
}

function isSessionReadOnly(session) {
	if (!session) return true;
	const st = String(session.status || "").toLowerCase();
	if (st === "ended" || st === "closed") return true;
	if (st === "waiting" || st === "active") return false;
	return String(session?.presenceStatus || "").toLowerCase() === "left";
}

function resolveEsSessionStatus(row) {
	const raw =
		row?.status != null && String(row.status).trim() !== ""
			? String(row.status).toLowerCase()
			: null;
	if (raw === "waiting" || raw === "active") return raw;
	if (raw === "ended" || raw === "closed") return "ended";
	return row?.roomId ? "waiting" : "ended";
}

function activeJoinStorageKey(websiteId) {
	return `liveChat.activeJoin.${websiteId}`;
}

function readPersistedActiveJoin(websiteId) {
	if (!websiteId || typeof sessionStorage === "undefined") return null;
	try {
		const raw = sessionStorage.getItem(activeJoinStorageKey(websiteId));
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed?.chatId || !parsed?.roomId) return null;
		return parsed;
	} catch {
		return null;
	}
}

function persistActiveJoin(websiteId, session) {
	if (!websiteId || typeof sessionStorage === "undefined" || !session) return;
	const chatId = chatIdOf(session);
	const roomId = roomIdOf(session);
	if (!chatId || !roomId || isHistoricalSession(session)) return;
	try {
		const prev = readPersistedActiveJoin(websiteId);
		sessionStorage.setItem(
			activeJoinStorageKey(websiteId),
			JSON.stringify({
				chatId,
				roomId,
				agentSessionId: session.agentSessionId || session.sessionId || null,
				customerName: session.customerName || null,
				customerEmail: session.customerEmail || null,
				visitorId: session.visitorId || null,
				status: "active",
				presenceStatus:
					session.presenceStatus ||
					prev?.presenceStatus ||
					"idle",
			})
		);
	} catch {
		/* ignore quota */
	}
}

function updatePersistedPresence(websiteId, chatId, presenceStatus) {
	if (!websiteId || !chatId || !presenceStatus) return;
	const saved = readPersistedActiveJoin(websiteId);
	if (!saved || String(saved.chatId) !== String(chatId)) return;
	try {
		sessionStorage.setItem(
			activeJoinStorageKey(websiteId),
			JSON.stringify({ ...saved, presenceStatus })
		);
	} catch {
		/* ignore */
	}
}

function clearPersistedActiveJoin(websiteId) {
	if (!websiteId || typeof sessionStorage === "undefined") return;
	try {
		sessionStorage.removeItem(activeJoinStorageKey(websiteId));
	} catch {
		/* ignore */
	}
}

function sessionSortRank(session) {
	if (isHistoricalSession(session)) return 0;
	const presence = String(session?.presenceStatus || "").toLowerCase();
	if (presence === "left") return 1;
	return 2;
}

function sessionSortTimestamp(session) {
	const raw =
		session?.createdAt ||
		session?.dateTime ||
		session?.joinedAt ||
		session?.timestamp ||
		null;
	if (!raw) return 0;
	const ts = new Date(raw).getTime();
	return Number.isFinite(ts) ? ts : 0;
}

function upsertSession(list, session) {
	const key = sessionMergeKey(session);
	if (!key) return list;
	const stamped = {
		...session,
		createdAt: session.createdAt || session.dateTime || session.joinedAt || new Date().toISOString(),
	};
	const idx = list.findIndex((s) => sessionMergeKey(s) === key);
	if (idx >= 0) {
		const existing = list[idx];
		const next = [...list];
		const existingHist = isHistoricalSession(existing);
		const incomingHist = isHistoricalSession(stamped);
		if (!existingHist && incomingHist) {
			next[idx] = {
				...stamped,
				...existing,
				customerName: existing.customerName || stamped.customerName,
				customerEmail: existing.customerEmail || stamped.customerEmail,
				agentSessionId: existing.agentSessionId || stamped.agentSessionId,
			};
		} else {
			const merged = { ...existing, ...stamped };
			const incomingStatus = String(stamped.status || "").toLowerCase();
			if (incomingStatus === "waiting" || incomingStatus === "active") {
				delete merged.source;
				merged.status = incomingStatus;
				merged.presenceStatus = stamped.presenceStatus || "online";
			} else if (stamped.presenceStatus == null || stamped.presenceStatus === "") {
				merged.presenceStatus = existing.presenceStatus;
			}
			if (!merged.createdAt) {
				merged.createdAt =
					existing.createdAt || existing.dateTime || stamped.createdAt;
			}
			next[idx] = merged;
		}
		return next;
	}
	return [stamped, ...list];
}

function applyPresenceToSessions(list, chatId, status) {
	if (!chatId || !status) return list;
	return list.map((s) => (chatIdOf(s) === chatId ? { ...s, presenceStatus: status } : s));
}

function mapEsHistorySession(row) {
	if (!row) return null;
	const st = resolveEsSessionStatus(row);
	const isEnded = st === "ended";
	return {
		chatId: row.chatId || null,
		roomId: row.roomId || null,
		agentSessionId: row.agentSessionId || row.sessionId || null,
		sessionId: row.sessionId || row.agentSessionId || null,
		visitorId: row.visitorId || null,
		customerName: row.customerName || null,
		customerEmail: row.customerEmail || null,
		pageUrl: row.pageUrl || null,
		firstMessage: row.firstMessage || null,
		dateTime: row.dateTime || null,
		status: st,
		presenceStatus: isEnded ? "left" : null,
	};
}

export function useLiveChatExecutive({ websiteId, userId, active }) {
	const [online, setOnline] = useState(false);
	const [wsConnected, setWsConnected] = useState(false);
	const [sessions, setSessions] = useState([]);
	const [sessionsLoader, setSessionsLoader] = useState(false);
	const [selectedSession, setSelectedSession] = useState(null);
	const [messages, setMessages] = useState([]);
	const [aiTurns, setAiTurns] = useState([]);
	const [messagesLoader, setMessagesLoader] = useState(false);
	const [draft, setDraft] = useState("");
	const [sending, setSending] = useState(false);
	const [unread, setUnread] = useState({});
	const [closeConfirm, setCloseConfirm] = useState({
		userConfirmed: false,
		agentConfirmed: false,
	});

	const inboxSocketRef = useRef(null);
	const presenceHeartbeatRef = useRef(null);
	const roomSocketRef = useRef(null);
	const selectedRef = useRef(null);
	const joinedChatIdRef = useRef(null);
	const restoreAttemptedRef = useRef(false);
	const rejoinNextJoinRef = useRef(false);

	selectedRef.current = selectedSession;

	const disconnectRoomSocket = useCallback((clearJoinKey = true) => {
		if (roomSocketRef.current) {
			roomSocketRef.current.disconnect();
			roomSocketRef.current = null;
		}
		if (clearJoinKey) joinedChatIdRef.current = null;
	}, []);

	const handlePresenceEvent = useCallback((data) => {
		const id = data?.chatId;
		const status = data?.presenceStatus || data?.status;
		if (!id || !status) return;
		updatePersistedPresence(websiteId, id, status);
		setSessions((prev) => applyPresenceToSessions(prev, id, status));
		setSelectedSession((prev) =>
			prev && chatIdOf(prev) === id ? { ...prev, presenceStatus: status } : prev
		);
	}, [websiteId]);

	const markSessionClosed = useCallback(
		(chatId) => {
			if (!chatId) return;
			clearPersistedActiveJoin(websiteId);
			setSessions((prev) =>
				prev.map((s) =>
					chatIdOf(s) === chatId ? { ...s, status: "ended", presenceStatus: "left" } : s
				)
			);
			if (chatIdOf(selectedRef.current) === chatId) {
				disconnectRoomSocket();
				setSelectedSession((prev) =>
					prev && chatIdOf(prev) === chatId
						? { ...prev, status: "ended", presenceStatus: "left" }
						: prev
				);
			}
		},
		[websiteId, disconnectRoomSocket]
	);

	const handleInboxEvent = useCallback(
		(event, data) => {
			if (event === "chat_waiting") {
				const session = inboxItemToSession(data, "waiting");
				if (session) {
					setSessions((prev) => upsertSession(prev, session));
					const id = chatIdOf(session);
					setSelectedSession((prev) =>
						prev && chatIdOf(prev) === id
							? { ...prev, ...session, status: "waiting", source: undefined }
							: prev
					);
				}
			} else if (event === "chat_active") {
				const session = inboxItemToSession(data, "active");
				if (session) {
					setSessions((prev) => upsertSession(prev, session));
					const id = chatIdOf(session);
					setSelectedSession((prev) =>
						prev && chatIdOf(prev) === id
							? { ...prev, ...session, status: "active", source: undefined }
							: prev
					);
				}
			} else if (event === "visitor_presence") {
				handlePresenceEvent(data);
			} else if (event === "close_confirm_update") {
				if (data?.chatId) {
					setCloseConfirm({
						userConfirmed: !!data.userConfirmed,
						agentConfirmed: !!data.agentConfirmed,
					});
				}
			} else if (event === "chat_ended") {
				setCloseConfirm({ userConfirmed: false, agentConfirmed: false });
				markSessionClosed(data?.chatId);
			}
		},
		[handlePresenceEvent, markSessionClosed]
	);

	const openRoomSocket = useCallback(
		(wsUrl, session) => {
			disconnectRoomSocket(false);
			if (!wsUrl || !session) return;

			const chatId = chatIdOf(session);
			joinedChatIdRef.current = chatId;

			const socket = createPieSocketClient({
				wsUrl,
				onEvent: (event, data) => {
					if (event === "chat_message") {
						const selectedId = chatIdOf(selectedRef.current);
						if (data?.chatId && selectedId && data.chatId !== selectedId) return;

						setMessages((prev) => upsertChatMessage(prev, data, { userId }));
					} else if (event === "visitor_presence") {
						handlePresenceEvent(data);
					} else if (event === "close_confirm_update") {
						setCloseConfirm({
							userConfirmed: !!data.userConfirmed,
							agentConfirmed: !!data.agentConfirmed,
						});
					} else if (event === "chat_ended") {
						setCloseConfirm({ userConfirmed: false, agentConfirmed: false });
						markSessionClosed(data?.chatId);
					}
				},
			});
			roomSocketRef.current = socket;
		},
		[disconnectRoomSocket, userId, handlePresenceEvent, markSessionClosed]
	);

	const loadHistoricalConversation = useCallback(
		async (session) => {
			const agentSessionId = session?.agentSessionId || session?.sessionId;
			const chatId = chatIdOf(session);
			if (!websiteId || !agentSessionId) {
				setMessages([]);
				setAiTurns([]);
				return;
			}

			disconnectRoomSocket();
			joinedChatIdRef.current = chatId || `hist:${agentSessionId}`;
			setMessagesLoader(true);
			setMessages([]);
			setAiTurns([]);
			try {
				const response = await PostData("ms1", "api/v1/aiassistant/shopping-assistant-conversation", {
					websiteId,
					sessionId: agentSessionId,
					visitorId: session.visitorId || undefined,
					includeLive: true,
					chatId: chatId || undefined,
				});
				if (response?.status === "success") {
					const turns = Array.isArray(response?.data?.turns) ? response.data.turns : [];
					const liveMessages = Array.isArray(response?.data?.liveMessages)
						? response.data.liveMessages
						: [];
					const history = liveMessages
						.map((row) =>
							normalizePieSocketMessage({
								text: row.message || row.text,
								from:
									row.senderType === "executive"
										? "agent"
										: row.senderType === "customer"
											? "user"
											: row.senderType || "system",
								timestamp: row.createdAt || row.timestamp,
							})
						)
						.filter(Boolean);
					setAiTurns(turns);
					setMessages(history);
				} else {
					setAiTurns([]);
					setMessages([]);
					toast.error("Failed to load conversation history");
				}
			} catch (err) {
				console.error(err);
				toast.error("Failed to load conversation history");
				setAiTurns([]);
				setMessages([]);
			} finally {
				setMessagesLoader(false);
			}
		},
		[websiteId, disconnectRoomSocket]
	);

	const joinSelectedSession = useCallback(
		async (session) => {
			const chatId = chatIdOf(session);
			const roomId = roomIdOf(session);
			const st = String(session?.status || "").toLowerCase();
			const canJoinLive =
				!!chatId && !!roomId && st !== "ended" && st !== "closed";

			if (!canJoinLive) {
				await loadHistoricalConversation(session);
				return;
			}

			if (!websiteId) {
				setMessages([]);
				return;
			}

			const asRejoin = rejoinNextJoinRef.current;
			rejoinNextJoinRef.current = false;

			setMessagesLoader(true);
			setMessages([]);
			setAiTurns([]);
			try {
				const res = await joinChat({ chatId, roomId, websiteId, rejoin: asRejoin });
				if (!res.ok) {
					toast.error(res.message || "Failed to join chat");
					setMessages([]);
					setAiTurns([]);
					clearPersistedActiveJoin(websiteId);
					return;
				}
				const data = res.data || {};
				const merged = {
					...session,
					roomId: data.roomId || roomId,
					status: "active",
					customerName: data.customerName || session.customerName,
					customerEmail: data.customerEmail || session.customerEmail,
					agentSessionId: data.agentSessionId || session.agentSessionId,
					presenceStatus: session.presenceStatus || "idle",
					source: undefined,
				};
				const history = (data.messages || [])
					.map((row) => normalizePieSocketMessage(row))
					.filter(Boolean);
				const turns = Array.isArray(data.aiTurns) ? data.aiTurns : [];
				setSessions((prev) => upsertSession(prev, merged));
				setSelectedSession(merged);
				setAiTurns(turns);
				setMessages(history);
				openRoomSocket(data.wsUrl, merged);
				persistActiveJoin(websiteId, merged);
			} catch (err) {
				console.error(err);
				toast.error("Failed to join chat");
				setMessages([]);
				setAiTurns([]);
			} finally {
				setMessagesLoader(false);
			}
		},
		[websiteId, openRoomSocket, loadHistoricalConversation]
	);

	const fetchEsHistory = useCallback(async () => {
		if (!websiteId) return;
		try {
			const response = await PostData("ms1", "api/v1/aiassistant/shopping-assistant-sessions", {
				websiteId,
				scope: "liveWithBot",
			});
			if (response?.status === "success" && Array.isArray(response?.data?.sessions)) {
				const mapped = response.data.sessions.map(mapEsHistorySession).filter(Boolean);
				setSessions((prev) => {
					let next = [...prev];
					for (const hist of mapped) {
						next = upsertSession(next, hist);
					}
					return next;
				});
			}
		} catch (err) {
			console.error("[fetchEsHistory]", err);
		}
	}, [websiteId]);

	const joinSelectedSessionRef = useRef(joinSelectedSession);
	joinSelectedSessionRef.current = joinSelectedSession;
	const joiningRef = useRef(false);

	// Connect inbox while page is active; do not wipe session state when briefly inactive
	useEffect(() => {
		if (!active || !websiteId || !userId) {
			if (inboxSocketRef.current) {
				inboxSocketRef.current.disconnect();
				inboxSocketRef.current = null;
			}
			disconnectRoomSocket();
			setOnline(false);
			setWsConnected(false);
			return;
		}

		let cancelled = false;
		setSessionsLoader(true);
		restoreAttemptedRef.current = false;

		(async () => {
			await fetchEsHistory();
			if (cancelled) return;

			const res = await getAgentToken(websiteId);
			if (cancelled) return;
			if (!res.ok) {
				toast.error(res.message || "Could not go online for live chat");
				setOnline(false);
				setSessionsLoader(false);
				return;
			}

			setOnline(true);
			const { wsUrl } = res.data || {};
			if (!wsUrl) {
				toast.error("Live chat WebSocket URL missing");
				setSessionsLoader(false);
				return;
			}

			if (inboxSocketRef.current) {
				inboxSocketRef.current.disconnect();
				inboxSocketRef.current = null;
			}

			const socket = createPieSocketClient({
				wsUrl,
				onEvent: (event, data) => handleInboxEvent(event, data),
			});
			inboxSocketRef.current = socket;

			socket.on("open", () => {
				setWsConnected(true);
				setSessionsLoader(false);
			});
			socket.on("close", () => setWsConnected(false));

			// Restore in-progress room after full remount (other pages)
			if (!restoreAttemptedRef.current) {
				restoreAttemptedRef.current = true;
				const saved = readPersistedActiveJoin(websiteId);
				if (saved?.chatId && saved?.roomId) {
					const restored = {
						chatId: saved.chatId,
						roomId: saved.roomId,
						agentSessionId: saved.agentSessionId || null,
						sessionId: saved.agentSessionId || null,
						visitorId: saved.visitorId || null,
						customerName: saved.customerName || null,
						customerEmail: saved.customerEmail || null,
						status: "active",
						presenceStatus: saved.presenceStatus || "idle",
					};
					rejoinNextJoinRef.current = true;
					setSessions((prev) => upsertSession(prev, restored));
					setSelectedSession(restored);
				}
			}

			setTimeout(() => {
				if (!cancelled) setSessionsLoader(false);
			}, 4000);
		})();

		return () => {
			cancelled = true;
			if (presenceHeartbeatRef.current) {
				clearInterval(presenceHeartbeatRef.current);
				presenceHeartbeatRef.current = null;
			}
			postAgentPresence(websiteId, true).catch(() => {});
			if (inboxSocketRef.current) {
				inboxSocketRef.current.disconnect();
				inboxSocketRef.current = null;
			}
			disconnectRoomSocket();
		};
	}, [active, websiteId, userId, handleInboxEvent, disconnectRoomSocket, fetchEsHistory]);

	useEffect(() => {
		if (!active || !websiteId || !online) {
			if (presenceHeartbeatRef.current) {
				clearInterval(presenceHeartbeatRef.current);
				presenceHeartbeatRef.current = null;
			}
			return;
		}
		postAgentPresence(websiteId, false).catch(() => {});
		presenceHeartbeatRef.current = setInterval(() => {
			postAgentPresence(websiteId, false).catch(() => {});
		}, 30_000);
		return () => {
			if (presenceHeartbeatRef.current) {
				clearInterval(presenceHeartbeatRef.current);
				presenceHeartbeatRef.current = null;
			}
		};
	}, [active, websiteId, online]);

	// Join / rejoin when selection changes or room socket is missing
	useEffect(() => {
		if (!selectedSession) {
			if (!readPersistedActiveJoin(websiteId)) {
				disconnectRoomSocket();
				setMessages([]);
				setAiTurns([]);
			}
			return;
		}

		const chatId = chatIdOf(selectedSession);
		if (chatId) {
			setUnread((prev) => {
				if (!prev[chatId]) return prev;
				const next = { ...prev };
				delete next[chatId];
				return next;
			});
		}

		const joinKey =
			chatId || `hist:${selectedSession.agentSessionId || selectedSession.sessionId}`;
		const roomMissing =
			!isHistoricalSession(selectedSession) &&
			!!roomIdOf(selectedSession) &&
			!roomSocketRef.current;

		if (joiningRef.current) return;
		if (joinedChatIdRef.current !== joinKey || roomMissing) {
			if (roomMissing && joinedChatIdRef.current === joinKey) {
				rejoinNextJoinRef.current = true;
			}
			joiningRef.current = true;
			Promise.resolve(joinSelectedSessionRef.current(selectedSession)).finally(() => {
				joiningRef.current = false;
			});
		}
	}, [selectedSession, disconnectRoomSocket, websiteId]);

	const visibleSessions = useMemo(() => {
		return sessions
			.filter(Boolean)
			.sort((a, b) => {
				const rankDiff = sessionSortRank(b) - sessionSortRank(a);
				if (rankDiff !== 0) return rankDiff;
				return sessionSortTimestamp(b) - sessionSortTimestamp(a);
			});
	}, [sessions]);

	const isReadOnly = isSessionReadOnly(selectedSession);

	const sendReply = useCallback(async () => {
		if (isSessionReadOnly(selectedSession)) return;
		const text = draft.trim();
		const chatId = chatIdOf(selectedSession);
		const roomId = roomIdOf(selectedSession);
		if (!text || !chatId || !roomId || !websiteId) return;

		const tempId = `tmp_${Date.now().toString(36)}`;
		const optimistic = {
			id: tempId,
			tempId,
			senderId: userId ? String(userId) : "agent",
			senderType: "executive",
			message: text,
			createdAt: new Date().toISOString(),
		};
		setMessages((prev) => [...prev, optimistic]);
		setDraft("");
		setSending(true);

		const res = await sendAgentMessage({ chatId, roomId, text, websiteId });
		if (!res.ok) {
			toast.error(res.message || "Failed to send message");
			setMessages((prev) => prev.filter((m) => m.id !== tempId));
			setDraft(text);
		}
		setSending(false);
	}, [draft, selectedSession, websiteId, userId]);

	const handleClose = useCallback(async () => {
		if (isSessionReadOnly(selectedSession)) return;
		const chatId = chatIdOf(selectedSession);
		const roomId = roomIdOf(selectedSession);
		if (!chatId || !roomId || !websiteId) return;
		if (
			!window.confirm(
				"Confirm you want to end this chat? The visitor must also confirm before the conversation closes.",
			)
		) {
			return;
		}
		const res = await endChat({ chatId, roomId, websiteId, confirmed: true });
		if (res.ok) {
			const finalized = res.data?.finalized;
			if (finalized) {
				toast.success("Chat ended");
				clearPersistedActiveJoin(websiteId);
				disconnectRoomSocket();
				setCloseConfirm({ userConfirmed: false, agentConfirmed: false });
			} else {
				setCloseConfirm({
					userConfirmed: !!res.data?.userConfirmed,
					agentConfirmed: !!res.data?.agentConfirmed,
				});
				toast.info("Waiting for the customer to confirm closing the chat.");
			}
		} else {
			toast.error(res.message || "Failed to end chat");
		}
	}, [selectedSession, websiteId, disconnectRoomSocket]);

	const withdrawClose = useCallback(async () => {
		const chatId = chatIdOf(selectedSession);
		const roomId = roomIdOf(selectedSession);
		if (!chatId || !roomId || !websiteId) return;
		const res = await endChat({ chatId, roomId, websiteId, confirmed: false });
		if (res.ok) {
			setCloseConfirm({ userConfirmed: false, agentConfirmed: false });
			toast.info("Close request withdrawn.");
		}
	}, [selectedSession, websiteId]);

	const liveMessagesForMerge = useMemo(
		() =>
			messages.map((m) => ({
				message: m.message,
				text: m.message,
				senderType: m.senderType,
				createdAt: m.createdAt,
				timestamp: m.createdAt,
			})),
		[messages]
	);

	const mergedThread = useMemo(
		() => mergeConversationThread(aiTurns, liveMessagesForMerge),
		[aiTurns, liveMessagesForMerge]
	);

	return {
		online,
		wsConnected,
		sessions: visibleSessions,
		sessionsLoader,
		selectedSession,
		setSelectedSession,
		messages,
		mergedThread,
		aiTurns,
		messagesLoader,
		draft,
		setDraft,
		sending,
		sendReply,
		handleClose,
		withdrawClose,
		closeConfirm,
		unread,
		isReadOnly,
	};
}
